'use client'

import { usePasswordSettings, useSetPasswordSettings } from '@repo/services'
import { ButtonLink } from '@repo/shared/components'
import { useErrorHandler, useTranslate } from '@repo/shared/hooks'
import { Card, Col, Row, Typography } from '@repo/ui/components'
import { useState } from 'react'
import { ChangePasswordForm } from './change-password.form'
import { ChangePasswordSuccess } from './change-password.success'
import {
  bytesToBase64,
  generateHash,
} from '@repo/services/src/auth/password-tools'
import { normalizeDigits } from '@repo/ui/lib'

interface ChangePasswordContainerProps {
  isSetPassword?: boolean
}

export const ChangePasswordContainer = (
  props: ChangePasswordContainerProps,
) => {
  const { isSetPassword = false } = props
  const { t } = useTranslate()
  const { errorHandler } = useErrorHandler()

  const { mutate: setPasswordSettings, isPending: isLoading } =
    useSetPasswordSettings()

  const { data: passwordSettingsData } = usePasswordSettings()

  const [isSuccess, setIsSuccess] = useState(false)

  if (isSuccess) {
    return (
      <div className='px-md'>
        <ChangePasswordSuccess isSetPassword={isSetPassword} />
      </div>
    )
  }

  const handleOnSuccess = async (data: { password: string }) => {
    const algo1 = {
      salt1: passwordSettingsData?.data?.algorithmData?.salt1 ?? '',
      salt2: passwordSettingsData?.data?.algorithmData?.salt2 ?? '',
      g: passwordSettingsData?.data?.algorithmData?.g ?? 0,
      p: passwordSettingsData?.data?.algorithmData?.p ?? '',
    }

    const password = btoa(normalizeDigits(data.password))

    const pwHash = await generateHash(algo1, password)
    const passwordHash = bytesToBase64(pwHash)

    setPasswordSettings(
      {
        passwordHash,
        hint: '',
      },
      {
        onSuccess: () => {
          setIsSuccess(true)
        },
        onError: errorHandler,
      },
    )
  }

  return (
    <div className='px-md'>
      <Card className='p-xl rounded-sm'>
        <Col className='gap-3xl'>
          <Row className='justify-between'>
            <Typography size='2xl' weight='bold'>
              {isSetPassword
                ? t('changePassword_title_set_password')
                : t('changePassword_title')}
            </Typography>
            <ButtonLink href='/profile/account/' size='sm'>
              {t('common_cancellation')}
            </ButtonLink>
          </Row>
          <ChangePasswordForm
            isSetPassword={isSetPassword}
            onSuccess={handleOnSuccess}
            isLoading={isLoading}
          />
        </Col>
      </Card>
    </div>
  )
}
